import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import {
  APP_MODAL_ROUTES,
  CREDIT_REQUEST_INVOICE_ROUTES,
  CREDIT_REQUEST_ROUTES,
} from '@/navigators/routes';
import {
  AppModalStackParams,
  CreditRequestInvoiceStackParams,
} from '@/navigators/types';
import {
  CompositeNavigationProp,
  NavigationAction,
  RouteProp,
} from '@react-navigation/native';
import { CreditRequestInvoiceItemsContent } from '@/components/credit-request/credit-request-invoice-items-content';
import { FormExitConfirmation } from '@/components/form-exit-confirmation';
import { PageContainer } from '@/components/page-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCreditRequestInvoiceItems } from '@/hooks/credit-requests/use-credit-request-invoice-items';
import { useCreditRequestInvoiceStore } from '@/stores/credit-request-invoice-store';

interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<
      CreditRequestInvoiceStackParams,
      CREDIT_REQUEST_INVOICE_ROUTES.CreditRequestInvoiceItems
    >,
    StackNavigationProp<AppModalStackParams, APP_MODAL_ROUTES.CreditRequest>
  >;
  route: RouteProp<
    CreditRequestInvoiceStackParams,
    CREDIT_REQUEST_INVOICE_ROUTES.CreditRequestInvoiceItems
  >;
}

export const CreditRequestInvoiceItemsScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { invoiceUUID, orderInvoiceUUID, orderUUID, title } = route.params;
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const pendingActionRef = useRef<NavigationAction | null>(null);
  const bypassGuardRef = useRef(false);
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID,
  } = useAuthenticatedUserContext();

  const { items } = useCreditRequestInvoiceItems({
    orderInvoiceUUID,
    restaurantUUID: selectedRestaurantUUID,
    supplierUUID,
  });
  const addBatchItem = useCreditRequestInvoiceStore((s) => s.addBatchItem);
  const batchItems = useCreditRequestInvoiceStore((s) => s.batchItems);
  const clearBatchItems = useCreditRequestInvoiceStore(
    (s) => s.clearBatchItems,
  );

  useEffect(() => {
    return navigation.addListener('beforeRemove', (e) => {
      if (bypassGuardRef.current || batchItems.length === 0) {
        return;
      }

      e.preventDefault();
      pendingActionRef.current = e.data.action;
      setShowExitConfirmation(true);
    });
  }, [navigation, batchItems.length]);

  const onConfirmExit = () => {
    bypassGuardRef.current = true;
    setShowExitConfirmation(false);

    clearBatchItems();

    if (pendingActionRef.current) {
      navigation.dispatch(pendingActionRef.current);
      pendingActionRef.current = null;
    } else {
      navigation.goBack();
    }
  };

  return (
    <PageContainer
      navigationHeaderProps={{
        centerTitle: true,
        onBackPress: navigation.goBack,
        responsive: true,
        showBackButton: true,
        showShadow: false,
        title,
      }}
    >
      <CreditRequestInvoiceItemsContent
        items={items}
        onNext={() => {
          navigation.navigate(
            CREDIT_REQUEST_INVOICE_ROUTES.CreditRequestInvoiceDetails,
            {
              invoiceUUID,
              orderInvoiceUUID,
              orderUUID,
              title,
            },
          );
        }}
        onSelectItem={(variantPack, searchResultItem, existingBatchLineItem) =>
          navigation.navigate(APP_MODAL_ROUTES.CreditRequest, {
            params: {
              confirmOnExit: true,
              eligibleForCredit: searchResultItem.eligible_for_credit,
              existingBatchLineItem,
              guaranteed: searchResultItem.guaranteed,
              itemDetail: variantPack,
              multiRequest: true,
              onSuccess: addBatchItem,
              orderInvoiceUUID,
            },
            screen: CREDIT_REQUEST_ROUTES.Base,
          })
        }
      />
      {showExitConfirmation ? (
        <FormExitConfirmation
          confirmTrackingData={{
            name: 'credit_request_invoice_items_screen.confirm_exit',
          }}
          message={i18n.t('exit_confirmation.message')}
          onConfirm={onConfirmExit}
          onSkip={() => setShowExitConfirmation(false)}
          skipTrackingData={{
            name: 'credit_request_invoice_items_screen.cancel_exit',
          }}
          title={i18n.t('credit_request.screen.exit_confirmation.title')}
        />
      ) : null}
    </PageContainer>
  );
};
